<template>
  <div class="access-config">
    <el-form
      :model="accessConfigForm"
      :rules="accessConfigRules"
      ref="accessConfigForm"
      label-width="160px"
      :inline-message="true"
      class="access-config-form rule-form"
    >
      <el-form-item prop="name" label="规则名称：">
        <el-input
          v-model="accessConfigForm.name"
          placeholder="请输入规则名称"
          :disabled="formType === 'SHOW'"
        ></el-input>
      </el-form-item>
      <el-form-item
        class="form-tip-top"
        prop="logPath"
        label="日志路径："
        :rules="accessConfigRules.logPath"
      >
        <el-input
          v-model="accessConfigForm.logPath"
          placeholder="请输入日志路径"
          :disabled="formType === 'SHOW'"
        ></el-input>
      </el-form-item>
      <el-form-item>
        <p class="form-tip">
          指定文件夹下所有符合文件名称的文件都会被监控到（包含所有层次的目录），文件名称可以是完整名，也支持通配符模式匹配。Linux文件路径只支持“/”开头，例：/home/logdir/demo.log
        </p>
      </el-form-item>

      <!-- start: Grok -->
      <access-grok-rule-creator
        v-if="logType === 'grok'"
        ref="accessGrokRuleCreator"
        :accessConfigForm="accessConfigForm"
        :formType="formType"
      ></access-grok-rule-creator>
      <!-- end: Grok -->

      <!-- start: 分隔符 -->
      <access-split-rule-creator
        v-if="logType === 'usplit'"
        ref="accessSplitRuleCreator"
        :accessConfigForm="accessConfigForm"
        :formType="formType"
      ></access-split-rule-creator>
      <!-- end: 分隔符 -->

      <!-- start: Nginx -->
      <access-nginx-rule-creator
        v-if="logType === 'nginx'"
        ref="accessNginxRuleCreator"
        :accessConfigForm="accessConfigForm"
        :formType="formType"
      ></access-nginx-rule-creator>
      <!-- end: Nginx -->

      <!-- start: Apache -->
      <access-apache-rule-creator
        v-if="logType === 'apache'"
        ref="accessApacheRuleCreator"
        :accessConfigForm="accessConfigForm"
        :formType="formType"
      ></access-apache-rule-creator>
      <!-- end: Apache -->
    </el-form>
  </div>
</template>

<script>
import { reqJudgeRule } from '../../../../api/log';
import Validator from '../../../../utils/Validator';
import RuleConfig from '../../../../models/RuleConfig';
import AccessGrokRuleCreator from './AccessGrokRuleCreator';
import AccessSplitRuleCreator from './AccessSplitRuleCreator';
import AccessNginxRuleCreator from './AccessNginxRuleCreator';
import AccessApacheRuleCreator from './AccessApacheRuleCreator';

export default {
  name: 'AccessConfigCreator',

  components: {
    AccessGrokRuleCreator,
    AccessSplitRuleCreator,
    AccessNginxRuleCreator,
    AccessApacheRuleCreator
  },

  props: {
    ruleData: {
      type: Object
    },
    formType: {
      type: String
    }
  },

  data() {
    const validatorRequired = (rule, value, cb) => {
      const validator = new Validator(rule, value, cb);
      validator.register(validator.required()).validate();
    };

    const validatorRuleName = (rule, value, cb) => {
      const validator = new Validator(rule, value, cb);
      validator
        .register(validator.isNoSpace())
        .register(validator.isNameStandardStr())
        .validate();
    };

    const checkRepeat = async (rule, value, cb) => {
      try {
        // 有 id 为编辑，不检查
        if (this.accessConfigForm.id) {
          cb();
          return;
        }

        const { data } = await reqJudgeRule({
          ruleName: value,
          logId: JSON.parse(sessionStorage.getItem('logData')).id
        });

        if (data) {
          cb(new Error('规则名称已存在'));
        } else {
          cb();
        }
      } catch {
        this.$message.error('网络连接超时，请稍候重试');
      }
    };

    const validatorLength = (rule, value, cb) => {
      const validator = new Validator(rule, value, cb);
      validator.register(validator.lenRange(50)).validate();
    };

    const validatorPath = (rule, value, cb) => {
      const validator = new Validator(rule, value, cb);
      validator.register(validator.isStartsWithSlash()).validate();
    };

    return {
      accessConfigForm: {
        name: '',
        logPath: '',
        pattern: '',
        logFormat: '',
        openStatistics: '',
        delimiterSeparator: '',
        customize: null,
        results: [
          {
            key: '',
            value: ''
          }
        ]
      },
      accessConfigRules: {
        name: [
          { required: true, message: '请输入规则名称', trigger: 'blur' },
          { validator: validatorRequired, message: '请输入规则名称', trigger: 'blur' },
          {
            validator: validatorRuleName,
            message: '规则名称只能输入字母、数字、下划线或中文',
            trigger: 'blur'
          },
          { validator: validatorLength, trigger: 'blur' },
          { validator: checkRepeat, trigger: 'blur' }
        ],
        logPath: [
          { required: true, message: '请输入日志路径', trigger: 'blur' },
          { validator: validatorRequired, message: '请输入日志路径', trigger: 'blur' },
          { validator: validatorPath, message: '日志路径路径只支持“/”开头', trigger: 'blur' }
        ]
      },
      formRelation: {
        grok: 'accessGrokRuleCreator',
        usplit: 'accessSplitRuleCreator',
        nginx: 'accessNginxRuleCreator',
        apache: 'accessApacheRuleCreator'
      }
    };
  },

  computed: {
    logType() {
      return sessionStorage.getItem('logType')
        ? JSON.parse(sessionStorage.getItem('logType')).name.toLowerCase()
        : this.accessConfigForm.logType
        ? this.accessConfigForm.logType.toLowerCase()
        : '';
    }
  },

  created() {
    this.getData();
  },

  methods: {
    /**
     * 本地存储数据，用来步骤切换时回显数据
     */
    getData() {
      // this.formType === 'SHOW' 表示查看日志规则，不能编辑
      if (this.formType === 'SHOW') {
        try {
          const metrics = JSON.parse(this.ruleData.metrics);
          this.accessConfigForm = new RuleConfig({
            id: metrics.id,
            name: this.ruleData.name,
            logPath: metrics.log_path,
            pattern: metrics.regex_value,
            logFormat: metrics.log_format,
            logExample: metrics.log_sample,
            delimiterSeparator: metrics.delimiter_separator,
            customize: metrics.customize,
            results: metrics.column_keys_values,
            logType: metrics.data_source_name
          });
        } catch (e) {
          this.accessConfigForm = new RuleConfig({});
        }
        return;
      }

      const accessConfigData = sessionStorage.getItem('accessConfigData')
        ? JSON.parse(sessionStorage.getItem('accessConfigData'))
        : new RuleConfig({});

      if (accessConfigData) {
        this.accessConfigForm = accessConfigData;
      }
    },
    /**
     * 表单校验通过后传递表单数据
     */
    onSaveData() {
      const relationForm = this.$refs[this.formRelation[this.logType]]
        ? this.$refs[this.formRelation[this.logType]].onSaveData()
        : null;

      if (!relationForm) {
        return this.accessConfigForm;
      }

      return Object.assign(this.accessConfigForm, relationForm);
    },
    /**
     * 点击下一步时检验表单
     */
    onSubmit() {
      const relationValidator = this.$refs[this.formRelation[this.logType]]
        ? this.$refs[this.formRelation[this.logType]].onValidate
        : null;

      if (!relationValidator) {
        return this.$refs.accessConfigForm.validate();
      }

      const validators = [this.$refs.accessConfigForm.validate(), relationValidator()];

      return Promise.all(validators);
    }
  }
};
</script>

<style lang="scss" scoped>
p {
  margin: 0;
}

a {
  text-decoration: none;
}

.access-config {
  &-form {
    width: 60%;
  }
}
</style>
